// @ts-check
/**
 * **演出の決まり**（索引 Issue #525。`duelEditor/core/` ＝ 舞台の決まりごと）。契約は `types.d.ts`。
 *
 * ## 1 ファイル = 1 状況 × 役割
 *
 * 実装は `themes/<theme>/effects/<situation>_<role>.js` に**状況と 1 対 1**で置く（`move_destroy_motion.js` / `new_turn_effect.js`）。
 * **ファイル名が規則**（どの状況で開くか・役割）なので、置けば出る・消せば出ない。
 * テーマごとに置くので「そのテーマでは出さない」は**ファイルを置かないだけ**。
 *
 * ## core は ocg を知らない。テーマも core のイベントを知らない
 *
 * `situation` は文字列としてしか見ない。**状況**は共通側の分岐レイヤー（`editorPorts/duelSituations/`）が
 * core の生イベントを包んで定義し直した単位（Issue #628。名前は平らな 1 段で、core のイベント名と同じでもよい）。
 * イベントの中身を意味のある名前に翻訳するのも分岐レイヤーの仕事で、テーマは `REASON_SYNCHRO` の数値も知らない。
 *
 * ## 宣言とコードが共存する
 *
 * 層は `part`（宣言。`parts/` の絵をキーフレームで動かす）か `script`（コード。three を直接使う）の
 * どちらか。**どちらも「オブジェクト」として名乗る**ので lil-gui に出る —— 宣言はノブが全部出て、
 * コードは「これはスクリプトです」と名前だけ出る。
 *
 * **`script` を持つ演出は尺を自分で返す義務がある**（宣言だけなら最後のキーから器が測れる）。
 */

import { checkPlacements } from '../mark';
import { EXTRA_EASES } from './motion';

/** @typedef {import('./types').EffectSpec} EffectSpec */
/** @typedef {import('./types').Effect} Effect */
/** @typedef {import('./types').Layer} Layer */
/** @typedef {import('./types').Anchor} Anchor */

/** 契約の口の一覧（`types.d.ts` の `EffectSpec` と 1 対 1） */
export const EFFECT_PORT = Object.freeze([
  'situation', 'role', 'label', 'opens', 'duration', 'places', 'clears', 'scene', 'layers', 'tweaks', 'hud', 'sounds', 'overlap', 'board', 'color',
]);

/**
 * **演出の役割**。1 つの状況を 2 つのファイルに分ける:
 *
 * - `motion` … **カードを動かす演出**（`<situation>_motion.js`）。カードそのものの描き直しと、
 *   描き直したカードの動きに付いていくもの（突進の残像・ぶつかった点から出る火花など）
 * - `effect` … **強調の演出**（`<situation>_effect.js`）。それ以外（カットイン・パネル・盤に残す印・照準の枠など）
 *
 * 2 つは**別の帯**になる（型の id は `effect` が状況の名前、`motion` が `<situation>_motion`）。
 * どちらも整列に加わり、同じ状況では motion → effect の順に並ぶ（`editorPorts/duelEffects.js::duelSpecs` が
 * motion の型を先に登録する）
 */
export const ROLES = Object.freeze(['motion', 'effect']);

/** 役割と状況の名前 → 帯の型の id */
export const roleTypeId = (situation, role = 'effect') => (role === 'motion' ? `${situation}_motion` : String(situation ?? ''));

/**
 * 帯の型の id → 状況の名前と役割
 * @param {string|null|undefined} type
 * @returns {{ situation: string, role: 'motion' | 'effect' }}
 */
export function roleOfType(type) {
  const t = String(type ?? '');
  return t.endsWith('_motion') ? { situation: t.slice(0, -'_motion'.length), role: 'motion' } : { situation: t, role: 'effect' };
}

/**
 * **演出の鍵**。状況の名前 ＋ motion なら `_motion`（`move_destroy` / `move_destroy_motion`）。
 * テーマの中で同じ鍵は 1 つ（`toTheme` が検める）。部品の名前（`fx:<鍵>/<層 id>`）にもなる
 */
export const effectKey = (e) => `${String(e?.situation ?? '')}${e?.role === 'motion' ? '_motion' : ''}`;

/**
 * **演出を引く**（状況の名前と役割で）。
 * @param {ReadonlyArray<Effect>} effects テーマの演出
 * @param {string} situation
 * @param {'motion'|'effect'} [role]
 * @returns {Effect|null}
 */
export function effectFor(effects, situation, role = 'effect') {
  return (effects ?? []).find((e) => e?.situation === situation && (e?.role ?? 'effect') === role) ?? null;
}

/**
 * **帯 → 演出**（型の id から役割、payload から状況を読んで引く）。器・状態チャンネル・ページはここで引く
 * @param {ReadonlyArray<Effect>} effects テーマの演出
 * @param {{ type?: string, payload?: any }|null|undefined} clip
 * @returns {Effect|null}
 */
export function effectOfClip(effects, clip) {
  const { situation, role } = roleOfType(clip?.type);
  return effectFor(effects, situation, role);
}

/** 関数でもよい口（`payload` で中身を変えられるもの） */
const FUNCTIONS = Object.freeze(['opens', 'duration', 'places', 'clears', 'tweaks', 'hud', 'sounds', 'overlap', 'board']);

/** 状況の名前 / 層の id に使ってよい綴り */
const SITUATION_NAME = /^[a-z][a-z0-9_]{0,39}$/;
const LAYER_ID = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;

/**
 * **置き場所 → どの空間か**。`at` の形の語彙はここが一次情報で、
 * 綴り間違いを黙って「画面の隅に出す」にしないために**知らない形は例外**にする。
 */
const ANCHOR_SPACE = Object.freeze({
  card: 'board',
  zone: 'board',
  hud: 'screen',
  screen: 'screen',
});

export const ANCHOR_KINDS = Object.freeze(Object.keys(ANCHOR_SPACE));

/**
 * `at` の形を検めて、どの空間に置くかを返す。
 * @param {any} at
 * @param {string} where エラーに出す場所
 * @returns {'board' | 'screen'}
 */
export function anchorSpace(at, where = 'at') {
  if (!at || typeof at !== 'object') throw new Error(`${where}: at は ${ANCHOR_KINDS.join(' / ')} のどれかです`);
  const kinds = ANCHOR_KINDS.filter((k) => k in at);
  if (kinds.length !== 1) {
    throw new Error(`${where}: at は ${ANCHOR_KINDS.join(' / ')} のどれか 1 つです（${kinds.join(' + ') || 'なし'}）`);
  }
  // `else`（引けなかったときの逃げ道。実写の Field は extra / hand を知らない）も同じ形
  if (at.else !== undefined) anchorSpace(at.else, `${where}.else`);
  return /** @type {'board' | 'screen'} */ (ANCHOR_SPACE[kinds[0]]);
}

/**
 * `board` の返り値（`{ at }` か null）を検める。`at` は 0 以上の数
 * @param {any} out
 * @param {string} where
 */
export function checkBoardCommit(out, where = 'board') {
  if (out == null) return;
  if (typeof out !== 'object' || !Number.isFinite(out.at) || out.at < 0) {
    throw new Error(`${where}: { at: 帯の頭からの ms（0 以上の数） } か null を返してください`);
  }
}

/** その層がコードで描くものか */
const isScript = (layer) => typeof layer?.script === 'function';

/**
 * 層 1 つを検める。**`part` と `script` はどちらか一方**（両方あると
 * 「宣言のノブを回しても絵が変わらない」という一番分かりにくい形になる）。
 *
 * @returns {{ part: string, label: string, space: 'board' | 'screen', front: boolean, script: boolean }}
 */
function checkLayer(situation, layer, seen, key = situation) {
  const where = `effect ${key}: layers`;
  if (!layer || typeof layer !== 'object') throw new Error(`${where}: 層は object です`);
  const { id } = layer;
  if (!LAYER_ID.test(String(id ?? ''))) throw new Error(`${where}: id が不正です（${id}）`);
  if (seen.has(id)) throw new Error(`${where}: id が重なっています（${id}）`);

  const declared = layer.part !== undefined;
  if (declared === isScript(layer)) {
    throw new Error(`${where}[${id}]: part（宣言）か script（コード）のどちらか一方です`);
  }

  /*
   * **親は自分より前に宣言されていること**（後ろ向きの参照を許すと合成の順が決められない）。
   * **`parent` と `at` は同時に書けない** —— 子は親の場所を受け継ぐので、両方あると
   * 「どちらが効くのか」が分からないまま片方が黙って死ぬ。
   */
  const parent = layer.parent === undefined ? null : seen.get(layer.parent);
  if (layer.parent !== undefined && !parent) {
    throw new Error(`${where}[${id}]: parent ${layer.parent} が先に宣言されていません`);
  }
  if (parent && layer.at !== undefined) {
    throw new Error(`${where}[${id}]: parent を持つ層は at を書きません（親の場所を受け継ぐ）`);
  }

  let space;
  if (declared) {
    if (typeof layer.part !== 'string' || !layer.part) throw new Error(`${where}[${id}]: part は素材の名前です`);
    // 親を持つ層は**親と同じ空間**（受け継ぐのは場所なので、盤の上か舞台のピクセルかも同じ）
    space = parent ? parent.space : anchorSpace(layer.at, `${where}[${id}].at`);
  } else {
    // **コードの層は自分で置く**（`stage` を持っているので `at` を渡さない）。どの空間かだけ名乗る
    space = parent ? parent.space : (layer.space ?? 'screen');
    if (space !== 'board' && space !== 'screen') throw new Error(`${where}[${id}]: space は board / screen です`);
  }
  if (layer.blend !== undefined && layer.blend !== 'normal' && layer.blend !== 'add') {
    throw new Error(`${where}[${id}]: blend は normal / add です`);
  }
  /*
   * **HUD より手前に出す層**（`front: true`。飛び出すカードなど）。舞台のピクセルの層だけ
   * （盤の上の板は透視で盤に貼るので HUD をまたぐ意味が無い）。親を持つ層は親と同じ側に出る
   * （親が手前で子が HUD の裏、では子が HUD に隠れて何の絵か分からない）。
   */
  if (layer.front !== undefined && typeof layer.front !== 'boolean') {
    throw new Error(`${where}[${id}]: front は true / false です`);
  }
  if (parent && layer.front !== undefined) {
    throw new Error(`${where}[${id}]: parent を持つ層は front を書きません（親と同じ側に出る）`);
  }
  const front = parent ? parent.front : Boolean(layer.front);
  if (front && space !== 'screen') throw new Error(`${where}[${id}]: front は space が screen の層だけです`);
  const out = {
    part: `fx:${key}/${id}`,
    label: layer.label ?? id,
    space,
    front,
    script: isScript(layer),
  };
  seen.set(id, out);
  return out;
}

/** `scene`（舞台への要求）の形を検める */
function checkScene(situation, scene) {
  if (scene === undefined || typeof scene === 'function') return;
  const list = scene === 'reset' ? [] : (Array.isArray(scene) ? scene : [scene]);
  if (scene !== 'reset' && !list.length) throw new Error(`effect ${situation}: scene が空です`);
  list.forEach((cfg) => {
    // **並びの中にも `'reset'` を書ける**（Issue #544。自分で変えて自分の帯の終わりで戻す演出）
    if (cfg === 'reset') return;
    if (!cfg || typeof cfg !== 'object') throw new Error(`effect ${situation}: scene は 'reset' か設定の object です`);
    if (cfg.field !== undefined && cfg.field !== 'live' && cfg.field !== 'virtual') {
      throw new Error(`effect ${situation}: scene.field は live / virtual です`);
    }
  });
}

/**
 * 演出を組み立てる。**どの演出もここを通る**ので口が欠けない。
 *
 * @param {EffectSpec} spec
 * @param {string} [file] ファイル名（`move_destroy_motion.js` / パスでもよい）。**`<situation>_<role>` と一致すること**
 * @returns {Effect}
 */
export function defineEffect(spec, file = '') {
  if (!spec || typeof spec !== 'object') throw new Error('defineEffect: spec は object です');
  const situation = String(spec.situation ?? '');
  if (!SITUATION_NAME.test(situation)) throw new Error(`defineEffect: situation が不正です（${spec.situation}）`);
  const role = spec.role === undefined ? 'effect' : spec.role;
  if (!ROLES.includes(role)) throw new Error(`defineEffect: role は ${ROLES.join(' / ')} です（${spec.role}）`);
  const key = effectKey({ situation, role });

  /*
   * **ファイル名と `situation` / `role` を突き合わせる**（Issue #525 の R2 / #628）。ファイル名が規則そのものなので、
   * ずれると「置いたのに出ない」になり、しかもどこにも警告が出ない。ファイルは `<situation>_<role>.js`（フォルダは切らない）
   */
  const base = String(file).split(/[\\/]/).filter(Boolean).pop()?.replace(/\.js$/, '') ?? '';
  if (base && base !== `${situation}_${role}`) {
    throw new Error(`defineEffect: ファイル名（${base}.js）と situation / role（${situation}_${role}）が違います`);
  }

  FUNCTIONS.forEach((port) => {
    if (spec[port] !== undefined && typeof spec[port] !== 'function') {
      throw new Error(`effect ${situation}: ${port} は関数です`);
    }
  });
  /*
   * **印の綴りは読み込みのときに検める**（Issue #534）。`places` / `clears` は関数なので
   * 中身は呼ばないと分からないが、**payload を見ないで返せるものは今ここで**弾ける ——
   * 綴り間違いが「実行して初めて分かる」のを減らす。
   */
  ['places', 'clears'].forEach((port) => {
    if (typeof spec[port] !== 'function') return;
    let out;
    try { out = spec[port]({ payload: {}, board: null, boardBefore: null, history: null, objects: null }); } catch { return; }
    checkPlacements(out, `effect ${situation}: ${port}`);
  });
  /*
   * **盤面を進める時刻の形**（Issue #566）。`{ at: ms }` か null。payload を見ないで返せるものは今ここで検める
   * （`at` の綴り間違い・文字列を黙って「帯の頭で進む」にしない）
   */
  if (typeof spec.board === 'function') {
    let out;
    let called = false;
    try { out = /** @type {any} */ (spec.board)({ payload: {}, board: null, boardBefore: null, history: null, objects: null }); called = true; } catch { /* payload が要るものは実行時に */ }
    if (called) checkBoardCommit(out, `effect ${situation}: board`);
  }
  Object.keys(spec).forEach((port) => {
    // **知らない口は弾く**（綴り間違いを黙って「何もしない」にしない）
    if (!EFFECT_PORT.includes(port)) throw new Error(`effect ${situation}: 知らない口 ${port}（${EFFECT_PORT.join(' / ')}）`);
  });
  checkScene(situation, spec.scene);

  const layers = spec.layers;
  if (layers !== undefined && typeof layers !== 'function' && !Array.isArray(layers)) {
    throw new Error(`effect ${situation}: layers は配列か関数です`);
  }

  /*
   * **部品は宣言から導く**（層が関数のときは組むまで分からないので空）。
   * `fx:<situation>[_motion]/<id>` がテーマの `parts` に混ざって lil-gui のフォルダになる（Issue #526）。
   */
  const seen = new Map();
  const parts = Array.isArray(layers) ? layers.map((l) => checkLayer(situation, l, seen, key)) : [];

  /*
   * **`script` を持つなら尺を返す義務**（Issue #525 の R5 / §8）——
   * コードの動きは器から測れないので、宣言が無ければ自動整列が長さを知りようがない。
   * 層が関数のときは組むまで分からないので、そのときも `duration` を要る扱いにする。
   */
  const hasScript = Array.isArray(layers) ? parts.some((p) => p.script) : layers !== undefined;
  if (hasScript && typeof spec.duration !== 'function') {
    throw new Error(`effect ${situation}: script を持つ演出は duration を返してください（尺は器から測れません）`);
  }

  return Object.freeze({
    ...spec,
    situation,
    role,
    label: spec.label ?? key,
    parts: Object.freeze(parts.map((p) => Object.freeze(p))),
  });
}

/**
 * **イージング**。名前と結果を揃えることが契約で、`index.test.js` が両端（0 と 1）と中点を固定している。
 */
export const EASES = Object.freeze({
  linear: (t) => t,
  hold: () => 0,
  ease_in: (t) => t * t,
  ease_out: (t) => 1 - (1 - t) ** 2,
  ease_in_out: (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2),
  // 行き過ぎて戻る・弾む・鋭く止まる（Issue #540。`motion.js` が一次情報）
  ...EXTRA_EASES,
});

/**
 * **キーフレームを解く**（`{ keys: [{ ms, value, ease }] }` か、値そのもの）。
 *
 * 範囲の外は端の値で止まる（**外挿しない**）。数でない値は補間せず**直前のキーの値**になる
 * （色を補間したいなら数の組で持つ）。
 *
 * @param {any} value
 * @param {number} ms 帯のローカル時間
 */
export function evaluateAt(value, ms = 0) {
  const keys = value?.keys;
  if (!Array.isArray(keys) || !keys.length) return value;
  const sorted = [...keys].sort((a, b) => (a?.ms ?? 0) - (b?.ms ?? 0));
  if (ms <= (sorted[0].ms ?? 0)) return sorted[0].value;
  const last = sorted[sorted.length - 1];
  if (ms >= (last.ms ?? 0)) return last.value;
  let i = 0;
  while (i < sorted.length - 1 && (sorted[i + 1].ms ?? 0) <= ms) i += 1;
  const a = sorted[i];
  const b = sorted[i + 1];
  const span = (b.ms ?? 0) - (a.ms ?? 0);
  if (!(span > 0)) return b.value;
  if (typeof a.value !== 'number' || typeof b.value !== 'number') return a.value;
  const ease = EASES[a.ease] ?? EASES.linear;
  return a.value + (b.value - a.value) * ease((ms - (a.ms ?? 0)) / span);
}

/** 尺を測れなかったときの長さ（`duelEditor/timeline/align.js` の既定と同じ） */
export const DEFAULT_DURATION_MS = 1000;

/** その値がキーフレームなら最後のキーの ms（違えば null） */
const lastKeyMs = (value) => {
  const keys = value?.keys;
  if (!Array.isArray(keys) || !keys.length) return null;
  return keys.reduce((a, k) => Math.max(a, k?.ms ?? 0), 0);
};

/**
 * **宣言から尺を測る**（Issue #532。設計文書 §8）。
 *
 * 宣言（`layers` の `tf` と `props`）の**最後のキーの ms** が尺。
 * `duration` を書かなくてよくなるのがこれで、**キーを足したら尺も伸びる**（食い違いようがない）。
 *
 * 測れないとき（キーが 1 つも無い / 層が関数）は `null`。
 * `script` を持つ演出は `defineEffect` が `duration` を要求しているので、ここは `null` でよい。
 *
 * @param {Effect|EffectSpec} effect
 * @returns {number|null}
 */
export function measureDuration(effect) {
  const layers = effect?.layers;
  if (!Array.isArray(layers)) return null;
  let max = null;
  layers.forEach((layer) => {
    [...Object.values(layer?.tf ?? {}), ...Object.values(layer?.props ?? {})].forEach((v) => {
      const ms = lastKeyMs(v);
      if (ms != null) max = Math.max(max ?? 0, ms);
    });
  });
  return max;
}

/**
 * その帯の尺。**演出の自己申告 → 宣言からの計測 → 既定** の順。
 *
 * @param {Effect} effect
 * @param {any} ctx 組み立てのときの context（`duration` に渡す）
 */
export function effectDuration(effect, ctx) {
  if (typeof effect?.duration === 'function') {
    const told = effect.duration(ctx);
    if (typeof told === 'number' && Number.isFinite(told) && told > 0) return told;
  }
  return measureDuration(effect) ?? DEFAULT_DURATION_MS;
}

/**
 * **尺と宣言の食い違いを集める**（純関数。Issue #532）。
 *
 * Issue #442 の連作で 5 回踏んだ「帯からはみ出して光ったまま瞬時に消える」がこれで編集前に分かる ——
 * 帯の終わりを過ぎた瞬間に器は演出を**フェード無しで丸ごと捨てる**ので、超えた分は
 * 「じわっと消える」のではなく光ったまま消える。
 *
 * @returns {Array<{code: string, level: 'warn'|'info', message: string}>}
 */
export function durationIssues(effect, durationMs) {
  /** @type {Array<{code: string, level: 'warn'|'info', message: string}>} */
  const out = [];
  const measured = measureDuration(effect);
  if (measured == null || !Number.isFinite(durationMs)) return out;
  if (measured > durationMs) {
    out.push({
      code: 'keys_outside_clip',
      level: 'warn',
      message: `${effect.situation}: キーが帯の外にある（最後のキー ${measured}ms > 尺 ${durationMs}ms）。帯の終わりで光ったまま消える`,
    });
  } else if (durationMs - measured > DEFAULT_DURATION_MS) {
    out.push({
      code: 'clip_longer_than_keys',
      level: 'info',
      message: `${effect.situation}: 帯が動きより ${durationMs - measured}ms 長い（最後のキー ${measured}ms / 尺 ${durationMs}ms）`,
    });
  }
  return out;
}

/** 素材（`duelEditor/parts/`）の口の一覧 */
export const PART_PORT = Object.freeze(['id', 'label', 'size', 'schema', 'style', 'blend', 'draw', 'needsCard']);

const PART_ID = /^[a-z][a-zA-Z0-9_]{0,39}$/;

/**
 * **素材を組み立てる**（Issue #528）。素材 ＝ **宣言の層が置く絵**。
 *
 * **素材は three を知らない。** 2D canvas に描くだけで、どこに・どの大きさで置くかは器が決める
 * （盤の層・オブジェクトの層と同じ作り）。three が要る絵は演出の `script` 層が
 * `ctx.THREE` を**受け取って**書くので、**`duelEditor` の three は `stage/three/` に閉じたまま**。
 *
 * `size` を省くと**アンカーの矩形の大きさ**になる（カードに重ねる印など）。
 *
 * @param {object} spec
 * @param {string} [file] ファイル名。**`id` と一致すること**
 */
export function definePart(spec, file = '') {
  if (!spec || typeof spec !== 'object') throw new Error('definePart: spec は object です');
  const id = String(spec.id ?? '');
  if (!PART_ID.test(id)) throw new Error(`definePart: id が不正です（${spec.id}）`);
  const base = String(file).split(/[\\/]/).pop()?.replace(/\.js$/, '') ?? '';
  if (base && base !== id) throw new Error(`definePart: ファイル名（${base}）と id（${id}）が違います`);
  Object.keys(spec).forEach((key) => {
    if (!PART_PORT.includes(key)) throw new Error(`part ${id}: 知らない口 ${key}（${PART_PORT.join(' / ')}）`);
  });
  if (typeof spec.draw !== 'function') throw new Error(`part ${id}: draw（2D canvas に描く）が要ります`);
  if (spec.size !== undefined && !(spec.size?.w > 0 && spec.size?.h > 0)) {
    throw new Error(`part ${id}: size は { w, h }（省くとアンカーの大きさ）`);
  }
  if (spec.blend !== undefined && spec.blend !== 'normal' && spec.blend !== 'add') {
    throw new Error(`part ${id}: blend は normal / add です`);
  }
  const schema = spec.schema ?? {};
  const style = spec.style ?? {};
  // **既定値は `style` が唯一の真実**（宣言にあって既定が無いと、GUI が undefined を回すことになる）
  Object.keys(schema).forEach((key) => {
    if (!(key in style)) throw new Error(`part ${id}: schema の ${key} に既定値（style）がありません`);
  });
  return Object.freeze({
    ...spec,
    id,
    label: spec.label ?? id,
    size: spec.size ?? null,
    blend: spec.blend ?? 'normal',
    /** カードの絵を引く素材か（器が絵を `deps` に入れて、読み込めたら描き直す） */
    needsCard: spec.needsCard === true,
    schema: Object.freeze({ ...schema }),
    style: Object.freeze({ ...style }),
  });
}

/** 演出の一覧 → テーマの `parts` に混ぜる形（`{ part, label, space }`） */
export function effectParts(effects = []) {
  return Object.freeze((effects ?? []).flatMap((e) => e?.parts ?? []));
}
